import { faRotateLeft, faTrashCan } from '@fortawesome/pro-light-svg-icons'
import { useCallback, useMemo } from 'react'
import { ButtonDisabledOrLoader } from '../../../../../../../../../../baseComponents/Button'
import { TwinIconToolTip } from '../../../../../../../../../../baseComponents/TwinIcon'
import { QuoteOrderCommentsButton, QuoteOrderDownloadButtonPDF, QuoteOrderDownloadPdfTemplateButton, QuoteOrderDuplicateButton, QuoteOrderRecalculateButton, QuoteOrderVersionsSelector } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderQuoteLayout/QuoteOrderButtons'
import { OrderData } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import { twinFetchPostText } from '../../../../../../../../../../utils/globals/data'
import { getBasePath } from '../../../../../../../../../../utils/globals/link'
import { checkQuoteFromOrderAllOk } from '../../../../../../../../../../utils/globals/order/order'
import useTwinTranslation from '../../../../../../../../../../utils/hooks/useTwinTranslation'
import { useLocation, useNavigate } from 'react-router'
import PermissionChecker from '../../../../../../../../../../baseComponents/PermissionChecker'
import { getUserInfo, getUserPermissions } from '../../../../../../../../../../utils/reducers/getters'
import useEditDeleteModal from '../../../../../../../../../../utils/hooks/useEditDeleteModal'
import ModalDeleteQuoteOrderDraft from '../ModalDeleteQuoteOrderDraft'
import ModalDeleteLastVersion from '../ModalDeleteLastVersion'
import { SellDownloadButtonCSV, SellPdfPreview, SellQuickAccessButton } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellLayout/SellButtons'
import { ButtonOrderInfoLeftBase, ButtonOrderInfoLeftBaseProps, OrderDivideIntoChaptersButton, OrderProductTypesButton, OrderVariablesButton } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtons'
import { OrderStepButtonsBase, OrderStepButtonsBaseComponentProps } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent/types'
import { OrderStepButtonsDefaultBaseComponent, OrderStepButtonsNewStyleBaseComponent } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent'
import useIsLoading from '../../../../../../../../../../utils/hooks/useIsLoading'

const QuoteOrderCreateStepButtons: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    const { t } = useTwinTranslation()
    const { infoMessageQuote } = useQuoteOrderCreateStepButtonsLogic({ data, onSubmit, ...rest })
    const OrderId = data?.header?.id
    const location = useLocation()
    return (
        <div className='flex items-center relative gap-5 flex-wrap'>
            <OrderStepButtonsDefaultBaseComponent {...{ data, onSubmit, hookFunction: useQuoteOrderCreateStepButtonsLogic, ...rest }} className='mr-8' />
            <QuoteOrderCreateButton title={t('processQuoteOrder', 'Procesar presupuesto')} disabled={infoMessageQuote ? true : false} OrderId={OrderId} onSubmit={onSubmit} status={data?.header.status} infoMessage={infoMessageQuote || ''} />
            <div className='absolute top-11 right-0 order_version_selector'>
                {data?.header?.version > 1 ? <QuoteOrderVersionsSelector url={getBasePath(location.pathname, 'single')} versions={data?.header?.versions} currentValue={data?.header?.id} /> : null}
            </div>
        </div>
    )
}

const useQuoteOrderCreateStepButtonsLogic = ({ data, onSubmit, orderHeaderURLs, setCompleteData, updateHeaderData, userPermissions }: OrderStepButtonsBaseComponentProps) => {
    const { t } = useTwinTranslation()
    const userInfo = getUserInfo()
    const name = 'quote_order_create_step_buttons'
    let infoMessageQuote = checkQuoteFromOrderAllOk(data, t)
    const Order = data?.header
    const EmployeeId = userInfo?.Employee?.id
    const orderEmployeeId = Order?.CreateEmployeeId
    const isMyQuote = orderEmployeeId === EmployeeId
    const hideDeleteVersionButton = !isMyQuote || Order?.version === 1
    const hideDeleteButton = !isMyQuote || Order?.version !== 1
    const hideSellQuickAccesButton = !Order?.QuoteOrderId && !Order?.SellOrderId && !Order?.SellDeliveryNoteId && !Order?.SellInvoiceId && !Order?.WorkSheetId && !Order?.relatedSellOrders?.length && !Order?.relatedSellDeliveryNotes?.length && !Order?.relatedSellInvoices?.length && !Order?.relatedWorkSheets?.length

    const defaultButtons = [{ "id": "previewQuote" }, { "id": "downloadQuote" }, { "id": "quoteOrderComments" }, { "id": "duplicateQuoteOrder" }, { "id": "deleteQuoteOrder" }]

    const quoteOrderDuplicateText = t('duplicateQuote', 'Duplicar presupuesto')
    const quoteOrderDeleteVersionText = t('revertVersion', 'Revertir versión')
    const quoteOrderDeleteButtonText = t('deleteDraft', 'Eliminar borrador')
    const quoteOrderRecalculateButtonText = t('recalculateQuote', 'Recalcular presupuesto')
    const sellDownloadCSVText = t('downloadCSV', 'Descargar CSV')
    const sellPdfPreviewText = t('previewPdf', 'Previsualizar PDF')
    const quoteOrderCommentsText = t('conversationWithCustomer', 'Conversación con el cliente')
    const sellQuickAccessText = t('quickAccess', 'Acceso rápido')
    const quoteOrderDownloadPdfTemplateText = t('downloadSelectingPdfTemplate', 'Descargar seleccionando plantilla PDF')
    const quoteOrderDownloadButtonPDFText = t('downloadPdf', 'Descargar PDF')
    const quoteOrderDivideIntoChaptersText = t('divideIntoChapters', 'Dividir en capítulos')
    const quoteOrderVariablesText = t('variables', 'Variables')
    const quoteOrderProductTypesText = t('valueProductTypes', 'Valor tipos de producto')


    const ButtonComponents: OrderStepButtonsBase = useMemo(() => {
        return {
            duplicateQuoteOrder: {
                component: QuoteOrderDuplicateButton,
                extraProps: {
                    id: 'duplicateQuoteOrder',
                    allRowData: data,
                    title: quoteOrderDuplicateText
                }
            },
            revertVersion: {
                component: QuoteOrderDeleteVersionButton,
                showButton: !hideDeleteVersionButton,
                extraProps: {
                    title: quoteOrderDeleteVersionText,
                    id: 'revertVersion',
                    allRowData: data,
                }
            },
            deleteQuoteOrder: {
                component: QuoteOrderDeleteButton,
                showButton: !hideDeleteButton,
                extraProps: {
                    title: quoteOrderDeleteButtonText,
                    id: 'deleteQuoteOrder',
                    allRowData: data,
                }
            },
            recalculateQuoteOrder: {
                component: QuoteOrderRecalculateButton,
                extraProps: {
                    title: quoteOrderRecalculateButtonText,
                    id: 'recalculateQuoteOrder',
                    allRowData: data,
                    onSubmit
                }
            },
            downloadQuoteCsv: {
                component: SellDownloadButtonCSV,
                extraProps: {
                    title: sellDownloadCSVText,
                    id: 'downloadQuoteCsv',
                    Order,
                    typeTemplate: 'quoteOrder'
                }
            },
            previewQuote: {
                component: SellPdfPreview,
                showButton: !infoMessageQuote,
                extraProps: {
                    title: sellPdfPreviewText,
                    id: 'previewQuote',
                    Order,
                    typeTemplate: 'quoteOrder'
                }
            },
            quoteOrderComments: {
                component: QuoteOrderCommentsButton,
                extraProps: {
                    title: quoteOrderCommentsText,
                    id: 'quoteOrderComments',
                    onSubmit,
                    allRowData: data
                }
            },
            quickAccess: {
                component: SellQuickAccessButton,
                showButton: !hideSellQuickAccesButton,
                extraProps: {
                    id: 'quickAccess',
                    Order,
                    title: sellQuickAccessText
                }
            },
            downloadQuotePDFTemplate: {
                component: QuoteOrderDownloadPdfTemplateButton,
                showButton: !infoMessageQuote,
                extraProps: {
                    title: quoteOrderDownloadPdfTemplateText,
                    id: 'downloadQuotePDFTemplate',
                    key: 'downloadQuotePDFTemplate',
                    Order
                }
            },
            downloadQuote: {
                component: QuoteOrderDownloadButtonPDF,
                showButton: !infoMessageQuote,
                extraProps: {
                    title: quoteOrderDownloadButtonPDFText,
                    id: 'downloadQuote',
                    Order
                }
            },
            divideIntoChaptersQuote: {
                component: OrderDivideIntoChaptersButton,
                extraProps: {
                    title: quoteOrderDivideIntoChaptersText,
                    id: 'divideIntoChaptersQuote',
                    allRowData: data,
                    userPermissions,
                    onSubmit,
                    orderHeaderURLs,
                    setCompleteData,
                    updateHeaderData
                }
            },
            variablesQuote: {
                component: OrderVariablesButton,
                extraProps: {
                    title: quoteOrderVariablesText,
                    id: 'variablesQuote',
                    allRowData: data,
                    userPermissions,
                    onSubmit,
                    orderHeaderURLs,
                    setCompleteData,
                    updateHeaderData
                }
            },
            quoteProductTypes: {
                component: OrderProductTypesButton,
                extraProps: {
                    title: quoteOrderProductTypesText,
                    id: 'quoteProductTypes',
                    allRowData: data,
                    userPermissions,
                    onSubmit,
                    orderHeaderURLs,
                    setCompleteData,
                    updateHeaderData,
                }
            },
        }
    }, [Order, data, infoMessageQuote, onSubmit, quoteOrderCommentsText, quoteOrderDeleteButtonText, quoteOrderDeleteVersionText, quoteOrderDownloadButtonPDFText, quoteOrderDownloadPdfTemplateText, quoteOrderDuplicateText, quoteOrderRecalculateButtonText, sellDownloadCSVText, sellPdfPreviewText, sellQuickAccessText, hideSellQuickAccesButton, hideDeleteButton, quoteOrderDivideIntoChaptersText, quoteOrderVariablesText, orderHeaderURLs, setCompleteData, updateHeaderData, userPermissions, hideDeleteVersionButton, quoteOrderProductTypesText])

    return { infoMessageQuote, ButtonComponents, name, defaultButtons }
}

export const QuoteOrderCreateStepButtonsNew: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    const { t } = useTwinTranslation()
    const { infoMessageQuote } = useQuoteOrderCreateStepButtonsLogic({ data, onSubmit, ...rest })
    const OrderId = data?.header?.id
    const location = useLocation()
    return (
        <div className='flex order_config_buttons_parent items-center relative gap-5 flex-wrap'>
            <OrderStepButtonsNewStyleBaseComponent {...{ data, onSubmit, hookFunction: useQuoteOrderCreateStepButtonsLogic, ...rest }} className='mr-8' />
            <QuoteOrderCreateButton title={t('processQuoteOrder', 'Procesar presupuesto')} disabled={infoMessageQuote ? true : false} OrderId={OrderId} onSubmit={onSubmit} status={data?.header.status} infoMessage={infoMessageQuote || ''} />
            <div className='absolute top-11 right-0 order_version_selector'>
                {data?.header?.version > 1 ? <QuoteOrderVersionsSelector url={getBasePath(location.pathname, 'single')} versions={data?.header?.versions} currentValue={data?.header?.id} /> : null}
            </div>
        </div>
    )
}

type QuoteOrderCreateButtonProps = QuoteOrderCreateButtonLogicProps & ButtonOrderInfoLeftBaseProps & {
    title: string
    children?: React.ReactNode
}

const QuoteOrderCreateButton: React.FC<QuoteOrderCreateButtonProps> = ({ title, OrderId, onSubmit, status, disabled, infoMessage, ...rest }) => {
    const { finalizeOrder, loading } = useQuoteOrderCreateButtonLogic({ OrderId, onSubmit, status })
    return (
        <ButtonOrderInfoLeftBase disabled={disabled} infoMessage={infoMessage} {...rest}>
            <div className='min-w-210'>
                <ButtonDisabledOrLoader onClick={finalizeOrder} buttonIsDisabled={disabled} textButton={title} loading={loading} className='w-full' />
            </div>
        </ButtonOrderInfoLeftBase>
    )
}

interface QuoteOrderCreateButtonLogicProps {
    onSubmit: () => void
    OrderId?: string
    status: number
}

const useQuoteOrderCreateButtonLogic = ({ onSubmit, OrderId, status }: QuoteOrderCreateButtonLogicProps) => {
    const { startLoading, endLoading, loading } = useIsLoading()
    const finalizeOrder = useCallback(async () => {
        startLoading?.()
        await twinFetchPostText('/api/app/order/quote/updateQuoteOrder', { id: OrderId, status: status + 1 })
        onSubmit()
        endLoading?.()
    }, [OrderId, onSubmit, status, startLoading, endLoading])

    return { finalizeOrder, loading }
}

interface QuoteOrderDeleteButtonProps {
    allRowData: OrderData
    title: string
    id: string
    children?: React.ReactNode
}

const QuoteOrderDeleteButton: React.FC<QuoteOrderDeleteButtonProps> = ({ allRowData, title, id, children }) => {
    const { openModal, setOpenModal, myOnSubmit } = useQuoteOrderDeleteButtonLogic()
    return (
        <PermissionChecker permission='delete' userPermissions={getUserPermissions('quote.order')}>
            <div onClick={() => setOpenModal({ type: 'delete', allRowData })}>
                {children ?? <TwinIconToolTip classNameIcon='hover:text-red-BA regular22' message={title} icon={faTrashCan} id={id} />}
            </div>
            {openModal?.type === 'delete' ? <ModalDeleteQuoteOrderDraft setOpened={() => setOpenModal(null)} allRowData={allRowData} onSubmit={myOnSubmit} /> : null}
        </PermissionChecker>
)
}

const useQuoteOrderDeleteButtonLogic = () => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    const navigate = useNavigate()
    const location = useLocation()
    const basePath = getBasePath(location.pathname, 'orders')

    const myOnSubmit = useCallback(() => {
        navigate(basePath)
    }, [navigate, basePath])
    return { openModal, setOpenModal, myOnSubmit }
}
interface QuoteOrderDeleteVersionButtonProps {
    allRowData: OrderData
    title: string
    id: string
    children?: React.ReactNode
}

const QuoteOrderDeleteVersionButton: React.FC<QuoteOrderDeleteVersionButtonProps> = ({ allRowData, title, id, children }) => {
    const { openModal, setOpenModal } = useQuoteOrderDeleteVersionButtonLogic()
    return (
        <PermissionChecker permission='delete' userPermissions={getUserPermissions('quote.order')}>
            <div onClick={() => setOpenModal({ type: 'delete', allRowData })}>
                {children ?? <TwinIconToolTip classNameIcon='hover:text-red-BA regular22' message={title} icon={faRotateLeft} id={id} />}
            </div>
            {openModal?.type === 'delete' ? <ModalDeleteLastVersion setOpened={() => setOpenModal(null)} allRowData={allRowData} onSubmit={() => { }} /> : null}
        </PermissionChecker>
    )
}

const useQuoteOrderDeleteVersionButtonLogic = () => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    return { openModal, setOpenModal }
}

export default QuoteOrderCreateStepButtons